import { MapFeatures } from "../types/MapFeatures";
import type { PullInZone } from "../types/PullInZone";

/** The subset of a Job this operation reads — usable from tests and from partial job data. */
export interface MapFeaturesCarrier {
	id: string;
	mapFeaturesGeoJson?: string;
}

/** True when the job carries at least one pull-in zone — committed in its field, or staged for it
 *  this session. A job in `clearedJobIds` has its committed zones staged for deletion, so those no
 *  longer count; zones drawn AFTER the clear was staged still do, which is why the staged check
 *  runs first. */
export function hasPullInZones(
	job: MapFeaturesCarrier,
	stagedByJobId?: ReadonlyMap<string, readonly PullInZone[]>,
	clearedJobIds?: ReadonlySet<string>,
): boolean {
	if ((stagedByJobId?.get(job.id)?.length ?? 0) > 0) return true;
	if (clearedJobIds?.has(job.id)) return false;
	return (MapFeatures.parse(job.mapFeaturesGeoJson)?.pullInZones().length ?? 0) > 0;
}

/**
 * How many of these jobs already carry a pull-in zone. Feeds the confirm dialog's "K of N already
 * have pull-in zones — this zone is added to them" line and the clear button's visibility — so a
 * job whose zones are already staged for clearing must drop out of K (there is nothing left to
 * clear), which is what `clearedJobIds` is for.
 */
export function countJobsWithPullInZones(
	jobs: readonly MapFeaturesCarrier[],
	stagedByJobId?: ReadonlyMap<string, readonly PullInZone[]>,
	clearedJobIds?: ReadonlySet<string>,
): number {
	let count = 0;
	for (const job of jobs) if (hasPullInZones(job, stagedByJobId, clearedJobIds)) count++;
	return count;
}
